import React, { useState } from 'react';
import { AtSign, Check, Loader2, User as UserIcon, LogOut, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../lib/api';
import { toast } from 'sonner';

export default function SettingsTab() {
  const { user, setUser, logout } = useAuth();
  const [handle, setHandle] = useState(user?.handle || '');
  const [name, setName] = useState(user?.name || '');
  const [tagline, setTagline] = useState(user?.profile?.tagline || '');
  const [bio, setBio] = useState(user?.profile?.bio || '');
  const [savingHandle, setSavingHandle] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const claim = async (e) => {
    e.preventDefault();
    setSavingHandle(true);
    try {
      const { data } = await API.put('/auth/handle', { handle });
      setUser(data);
      toast.success('Handle claimed!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not claim handle'); }
    finally { setSavingHandle(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await API.put('/creator/settings', { name, profile: { tagline, bio } });
      setUser(data);
      toast.success('Profile saved');
    } catch { toast.error('Could not save'); }
    finally { setSavingProfile(false); }
  };

  return (
    <div className="space-y-6">
      {/* Handle */}
      <div className="glass rounded-2xl p-5">
        <div className="mb-1 flex items-center gap-2">
          <AtSign className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-semibold text-white">Your custom handle</h3>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">This is your public storefront URL.</p>
        <form onSubmit={claim} className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">biocloser.com/</span>
            <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="yourname" className="rounded-xl border-white/10 bg-white/[0.04] pl-[7.5rem] focus-visible:ring-primary" />
          </div>
          <Button type="submit" disabled={savingHandle} className="rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {savingHandle ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Claim <Check className="ml-1 h-4 w-4" /></>}
          </Button>
        </form>
        {user?.handle && (
          <a href={`/${user.handle}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
            <ExternalLink className="h-3 w-3" /> View your store at biocloser.com/{user.handle}
          </a>
        )}
      </div>

      {/* Profile */}
      <div className="glass rounded-2xl p-5">
        <div className="mb-3 flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-semibold text-white">Store profile</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Tagline</Label>
            <Input value={tagline} onChange={(e) => setTagline(e.target.value)} className="rounded-xl border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Bio</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className="rounded-xl border-white/10 bg-white/[0.04] focus-visible:ring-primary" />
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} className="rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save profile'}
          </Button>
        </div>
      </div>

      {/* Account */}
      <div className="glass flex items-center justify-between rounded-2xl p-5">
        <div>
          <p className="text-sm font-medium text-white">{user?.email}</p>
          <p className="text-xs text-muted-foreground">Signed in · {user?.plan === 'pro' ? 'Pro plan' : 'Free plan'}</p>
        </div>
        <Button onClick={logout} variant="ghost" className="rounded-full text-white/70 hover:bg-white/10 hover:text-white">
          <LogOut className="mr-1.5 h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}
